/**
 * Arquivo ÚNICO de rotas (FUNIL e MÉTRICAS) para o frontend.
 * Pode ser incorporado ao final do seu `api.ts`, ou num arquivo como `metricsApi.ts`.
 */

import api from './api.ts'; // <-- importamos seu axios já configurado
import { AxiosError } from 'axios';

/* --------------------------------------------------------------------------
 * 1) Interfaces compartilhadas
 * -------------------------------------------------------------------------- */

// Já existentes em metrics_funnels.py

// 1.1) /metrics/funnels
export interface FunnelMetricsResponse {
  totalLeads: number;
  totalAgendamentos: number;
  percentAgendamentos: number;
  totalComparecimentos: number;
  percentComparecimentos: number;
  totalVendas: number;
  percentVendas: number;
  valorFaturado: number;
  valorPago: number;
  valorOrcado: number;
  ticketMedio: number;
  boasPraticas: string[];
  agendamentosPorDataConsulta?: {
    total: number;
    percentual: number;
  };
}

export interface FunnelMetricsParams {
  clinicId?: number;
  startDate?: string; // 'YYYY-MM-DD'
  endDate?: string;   // 'YYYY-MM-DD'
}

// 1.2) /metrics/funnel_by_source
export interface FunnelBySourceItem {
  fonte: string;       // "Meta Ads", "Orgânico", ou o valor de source_id
  totalLeads: number;
}

// 1.3) /metrics/timeline
export interface TimelineEvent {
  entity_id: number;
  event_date: string;    // ex.: "2025-03-01T10:00:00Z"
  event_type: string;    // "novo_lead", "agendamento", "comparecimento", etc.
  descricao: string;     // alguma descrição, ex.: nome do lead/paciente
}

// 1.4) /metrics/projections
export interface ProjectionsResponse {
  mes: string;               // ex. "2025-03"
  diasNoMes: number;
  diaAtual: number;
  leadsSoFar: number;
  leadsProjection: number;
  agendamentosSoFar: number;
  agendamentosProjection: number;
  comparecimentosSoFar: number;
  comparecimentosProjection: number;
  vendasSoFar: number;
  vendasProjection: number;
  faturadoSoFar: number;
  faturadoProjection: number;
}

// 1.5) /metrics/time_between_stages
export interface TimeBetweenStagesResponse {
  leadToAgendamento: number;        // em dias
  leadToComparecimento: number;     // em dias
  leadToVenda: number;              // em dias
}

/* --------------------------------------------------------------------------
 * 2) Interfaces das NOVAS Rotas
 * -------------------------------------------------------------------------- */

// 2.1) /metrics/funnels/timeseries
export interface TimeseriesItem {
  month: string;               // "YYYY-MM"
  totalLeads: number;
  totalAgendamentos: number;
  totalComparecimentos: number;
  totalVendas: number;
}

// 2.2) /metrics/ai_vs_humano
export interface IAvsHumanoResponse {
  agendamentos: { ia: number; humano: number };
  comparecimentos: { ia: number; humano: number };
  vendas: { ia: number; humano: number };
  faturamento: { ia: number; humano: number };
}

// 2.3) /metrics/time_distribution
export interface TimeDistributionItem {
  hora: string;   // ex.: "8-9"
  leads: number;
  agendamentos: number;
}

// 2.4) /metrics/weekly_heatmap
export interface WeeklyHeatmapItem {
  diaSemana: number;   // 0=domingo, 1=segunda, ...
  faixa: string;       // "8-10", "10-12", ...
  totalLeads: number;
}

/* --------------------------------------------------------------------------
 * 3) Funções de API existiam no metrics_funnels.py
 * -------------------------------------------------------------------------- */

/**
 * GET /api/metrics/funnels
 * Retorna métricas de funil (Leads, Agendamentos, Comparecimentos, Vendas, etc.)
 */
export async function getFunnelMetrics(params: FunnelMetricsParams): Promise<FunnelMetricsResponse> {
  try {
    const qs = new URLSearchParams();
    if (params.clinicId) qs.append('clinic_id', params.clinicId.toString());
    if (params.startDate) qs.append('start_date', params.startDate);
    if (params.endDate) qs.append('end_date', params.endDate);

    const url = `/api/metrics/funnels?${qs.toString()}`;
    const resp = await api.get<FunnelMetricsResponse>(url);
    return resp.data;
  } catch (error) {
    console.error('[getFunnelMetrics] Erro ao obter métricas do funil:', error);
    throw error;
  }
}

/**
 * GET /api/metrics/funnel_by_source
 * Retorna a quantidade de leads por fonte, considerando as regras de "Meta Ads" e "Orgânico".
 */
export async function getFunnelBySource(
  clinicId?: number,
  startDate?: string,
  endDate?: string
): Promise<FunnelBySourceItem[]> {
  const qs = new URLSearchParams();
  if (clinicId) qs.append('clinic_id', clinicId.toString());
  if (startDate) qs.append('start_date', startDate);
  if (endDate) qs.append('end_date', endDate);

  const url = `/api/metrics/funnel_by_source?${qs.toString()}`;
  try {
    const resp = await api.get<FunnelBySourceItem[]>(url);
    return resp.data;
  } catch (error) {
    console.error('[getFunnelBySource] Erro ao obter dados:', error);
    throw error;
  }
}

/**
 * GET /api/metrics/timeline
 * Retorna uma "linha do tempo" de eventos (Leads, Agendamentos, Comparecimentos, NoShow, Vendas).
 */
export async function getTimeline(
  clinicId?: number,
  startDate?: string,
  endDate?: string,
  limit: number = 20
): Promise<TimelineEvent[]> {
  const qs = new URLSearchParams();
  if (clinicId) qs.append('clinic_id', clinicId.toString());
  if (startDate) qs.append('start_date', startDate);
  if (endDate) qs.append('end_date', endDate);
  if (limit) qs.append('limit', limit.toString());

  const url = `/api/metrics/timeline?${qs.toString()}`;
  try {
    const resp = await api.get<TimelineEvent[]>(url);
    return resp.data;
  } catch (error) {
    console.error('[getTimeline] Erro ao obter timeline:', error);
    throw error;
  }
}

/**
 * GET /api/metrics/projections
 * Retorna projeções mensais com base no desempenho até agora.
 */
export async function getProjections(clinicId?: number): Promise<ProjectionsResponse> {
  const qs = new URLSearchParams();
  if (clinicId) qs.append('clinic_id', clinicId.toString());

  const url = `/api/metrics/projections?${qs.toString()}`;
  try {
    const resp = await api.get<ProjectionsResponse>(url);
    return resp.data;
  } catch (error) {
    console.error('[getProjections] Erro ao obter projeções:', error);
    throw error;
  }
}

/**
 * GET /api/metrics/time_between_stages
 * Calcula tempo médio (em dias) para Lead -> Agendamento, Comparecimento, Venda.
 */
export async function getTimeBetweenStages(
  clinicId?: number,
  startDate?: string,
  endDate?: string
): Promise<TimeBetweenStagesResponse> {
  const qs = new URLSearchParams();
  if (clinicId) qs.append('clinic_id', clinicId.toString());
  if (startDate) qs.append('start_date', startDate);
  if (endDate) qs.append('end_date', endDate);

  const url = `/api/metrics/time_between_stages?${qs.toString()}`;
  try {
    const resp = await api.get<TimeBetweenStagesResponse>(url);
    return resp.data;
  } catch (error) {
    console.error('[getTimeBetweenStages] Erro ao obter tempos médios:', error);
    throw error;
  }
}

/* --------------------------------------------------------------------------
 * 4) Funções de API das NOVAS Rotas
 * -------------------------------------------------------------------------- */

/**
 * GET /api/metrics/funnels/timeseries
 * Retorna uma série temporal (agrupada por mês) de Leads, Agendamentos,
 * Comparecimentos e Vendas, em um único array.
 */
export async function getFunnelTimeseries(
  clinicId?: number,
  startDate?: string,
  endDate?: string
): Promise<TimeseriesItem[]> {
  const qs = new URLSearchParams();
  if (clinicId) qs.append("clinic_id", clinicId.toString());
  if (startDate) qs.append("start_date", startDate);
  if (endDate) qs.append("end_date", endDate);

  const url = `/api/metrics/funnels/timeseries?${qs.toString()}`;
  try {
    const resp = await api.get<TimeseriesItem[]>(url);
    return resp.data;
  } catch (error) {
    console.error("[getFunnelTimeseries] Erro:", error);
    throw error;
  }
}

/**
 * GET /api/metrics/ai_vs_humano
 * Retorna estatísticas (agendamentos, comparecimentos, vendas + faturamento)
 * separadas entre IA x Humano.
 */
export async function getAiVsHumano(
  clinicId?: number,
  startDate?: string,
  endDate?: string
): Promise<IAvsHumanoResponse> {
  const qs = new URLSearchParams();
  if (clinicId) qs.append("clinic_id", clinicId.toString());
  if (startDate) qs.append("start_date", startDate);
  if (endDate) qs.append("end_date", endDate);

  const url = `/api/metrics/ai_vs_humano?${qs.toString()}`;
  try {
    const resp = await api.get<IAvsHumanoResponse>(url);
    return resp.data;
  } catch (error) {
    console.error("[getAiVsHumano] Erro:", error);
    throw error;
  }
}

/**
 * GET /api/metrics/time_distribution
 * Retorna a quantidade de Leads e Agendamentos agrupados por hora do dia.
 */
export async function getTimeDistribution(
  clinicId?: number,
  startDate?: string,
  endDate?: string
): Promise<TimeDistributionItem[]> {
  const qs = new URLSearchParams();
  if (clinicId) qs.append("clinic_id", clinicId.toString());
  if (startDate) qs.append("start_date", startDate);
  if (endDate) qs.append("end_date", endDate);

  const url = `/api/metrics/time_distribution?${qs.toString()}`;
  try {
    const resp = await api.get<TimeDistributionItem[]>(url);
    return resp.data;
  } catch (error) {
    console.error("[getTimeDistribution] Erro:", error);
    throw error;
  }
}

/**
 * GET /api/metrics/weekly_heatmap
 * Retorna dados de leads agregados por dia da semana + faixa de horas (Heatmap).
 */
export async function getWeeklyHeatmap(
  clinicId?: number,
  startDate?: string,
  endDate?: string
): Promise<WeeklyHeatmapItem[]> {
  const qs = new URLSearchParams();
  if (clinicId) qs.append("clinic_id", clinicId.toString());
  if (startDate) qs.append("start_date", startDate);
  if (endDate) qs.append("end_date", endDate);

  const url = `/api/metrics/weekly_heatmap?${qs.toString()}`;
  try {
    const resp = await api.get<WeeklyHeatmapItem[]>(url);
    return resp.data;
  } catch (error) {
    console.error("[getWeeklyHeatmap] Erro:", error);
    throw error;
  }
}
